import React, { useEffect, useMemo, useState } from "react";
import { Activity, Plus, Loader2, X, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";
import { api, fmtDate } from "../lib/api";
import { useAuth } from "../lib/auth";

// Hoisted styles so Recharts gets stable prop references.
const TICK = { fill: "#94A3B8", fontSize: 11, fontFamily: "JetBrains Mono" };
const READINESS_DOMAIN = [0, 100];
const TT_STYLE = {
  backgroundColor: "#FCD34D", border: "1px solid #B45309",
  borderRadius: 10, color: "#111827",
  fontFamily: "JetBrains Mono, monospace", fontSize: 12,
};
const TT_LABEL = { color: "#7C2D12", fontWeight: 600 };
const READINESS_DOT = { r: 3, strokeWidth: 0, fill: "#10B981" };
const READINESS_ACTIVE = { r: 6 };
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 4 };
const SCALE_LABELS = {
  sleep_quality: ["Awful", "Poor", "Okay", "Good", "Great"],
  energy:        ["Exhausted", "Tired", "Okay", "Energized", "Peak"],
  soreness:      ["None", "Mild", "Some", "Heavy", "Severe"],
  mood:          ["Low", "Off", "Okay", "Good", "Great"],
};

function zoneColor(score) {
  if (score == null) return "#94A3B8";
  if (score >= 75) return "#10B981";   // emerald — fresh
  if (score >= 50) return "#F59E0B";   // amber  — okay
  return "#EF4444";                    // red    — needs recovery
}

/**
 * Wellness check-in card on the Athlete Trend page. Lets coaches log one
 * day's wellness (sleep / energy / soreness / mood) for an athlete and
 * shows the resulting Readiness score over time.
 *
 * Readiness is computed server-side from up to four 1-5 scores rescaled
 * to 0-100. Higher = better. We plot it as a line + a 50/75 reference
 * band so abnormal dips are immediately visible.
 */
export default function WellnessSection({ sailorId, sailorName }) {
  const { hasPerm } = useAuth();
  const canAdd = hasPerm("add");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const reload = () => {
    if (!sailorId) return;
    setLoading(true);
    api.get(`/sailors/${sailorId}/wellness`)
      .then((r) => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [sailorId]);

  const chartData = useMemo(
    () => entries.map((e) => ({
      date: e.date,
      label: fmtDate(e.date),
      readiness: e.readiness,
    })),
    [entries],
  );
  const latest = entries[entries.length - 1];

  return (
    <div className="card p-5 md:p-6" data-testid="wellness-section">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <Activity size={12} className="text-emerald-300" /> Daily wellness · Readiness
          </div>
          <div className="font-display text-lg text-slate-100 mt-0.5">
            {latest != null
              ? <>Latest <span style={{ color: zoneColor(latest.readiness) }}>{latest.readiness}</span><span className="text-slate-500 text-sm font-normal ml-1">/100 on {fmtDate(latest.date)}</span></>
              : "No check-ins yet"}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            Composite of sleep · energy · soreness · mood. Lower scores = needs recovery; higher = ready.
          </div>
        </div>
        {canAdd && (
          <button
            onClick={() => setShowForm(true)}
            className="pill active !text-[11px]"
            data-testid="wellness-add-btn"
          >
            <Plus size={12} /> Log today
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-6 text-center text-slate-500 text-sm">
          <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : chartData.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-700 p-6 text-center text-sm text-slate-400">
          {canAdd
            ? <>No wellness data yet. Click <strong>Log today</strong> to start tracking {sailorName?.split(" ")[0] || "this athlete"}&apos;s readiness.</>
            : "No wellness check-ins recorded for this athlete yet."}
        </div>
      ) : (
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1E304B" />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={TICK}
                     interval="preserveStartEnd" minTickGap={20} />
              <YAxis axisLine={false} tickLine={false} tick={TICK}
                     domain={READINESS_DOMAIN} tickFormatter={(v) => `${v}`} width={36} />
              <ReferenceLine y={75} stroke="#10B981" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine y={50} stroke="#F59E0B" strokeDasharray="4 4" strokeOpacity={0.4} />
              <Tooltip contentStyle={TT_STYLE} labelStyle={TT_LABEL}
                       formatter={(v) => [`${v} / 100`, "Readiness"]} />
              <Line type="monotone" dataKey="readiness"
                    stroke={zoneColor(latest?.readiness)}
                    strokeWidth={2.5} dot={READINESS_DOT} activeDot={READINESS_ACTIVE}
                    connectNulls isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {showForm && (
        <WellnessForm
          sailorId={sailorId}
          existing={latest && latest.date === new Date().toISOString().slice(0, 10) ? latest : null}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); reload(); }}
        />
      )}
    </div>
  );
}

function Slider({ value, onChange, name, testid }) {
  const labels = SCALE_LABELS[name] || ["1", "2", "3", "4", "5"];
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? null : n)}
          className={`flex-1 min-w-[58px] px-2 py-1.5 rounded-md border text-[11px] font-mono-data transition-colors ${
            value === n
              ? "border-cyan-500/60 bg-cyan-500/20 text-cyan-100"
              : "border-[#1E304B] bg-[#0E1A2E] text-slate-400 hover:border-slate-600"
          }`}
          data-testid={`${testid}-${n}`}
        >
          <div className="text-sm">{n}</div>
          <div className="text-[9px] opacity-80 mt-0.5">{labels[n - 1]}</div>
        </button>
      ))}
    </div>
  );
}

function WellnessForm({ sailorId, existing, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(existing?.date || today);
  const [sleepHours, setSleepHours] = useState(existing?.sleep_hours ?? "");
  const [sleepQ, setSleepQ] = useState(existing?.sleep_quality ?? null);
  const [energy, setEnergy] = useState(existing?.energy ?? null);
  const [soreness, setSoreness] = useState(existing?.soreness ?? null);
  const [mood, setMood] = useState(existing?.mood ?? null);
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    if (!sleepQ && !energy && !soreness && !mood && !sleepHours) {
      setError("Fill at least one field");
      return;
    }
    setSaving(true); setError("");
    try {
      await api.post(`/sailors/${sailorId}/wellness`, {
        date,
        sleep_hours: sleepHours === "" ? null : Number(sleepHours),
        sleep_quality: sleepQ,
        energy,
        soreness,
        mood,
        notes: notes.trim() || null,
      });
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-8 px-4"
      data-testid="wellness-form-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="card w-full max-w-lg p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 flex items-center gap-1.5">
              <Activity size={11} /> Wellness check-in
            </div>
            <div className="font-display text-lg text-slate-100 mt-0.5">
              {existing ? "Update" : "Log"} {date === today ? "today" : fmtDate(date)}
            </div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="wellness-form-close">
            <X size={14} />
          </button>
        </div>

        {error && (
          <div className="card !bg-rose-500/10 !border-rose-500/30 px-3 py-2 text-sm text-rose-200 mb-3 flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Date</label>
            <input
              type="date"
              value={date}
              max={today}
              onChange={(e) => setDate(e.target.value)}
              className="form-input mt-1 w-full"
              data-testid="wellness-date"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Sleep hours (last night)</label>
            <input
              type="number" min="0" max="24" step="0.5"
              value={sleepHours}
              onChange={(e) => setSleepHours(e.target.value)}
              placeholder="e.g. 7.5"
              className="form-input mt-1 w-full no-spinner"
              data-testid="wellness-sleep-hours"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Sleep quality</label>
            <Slider value={sleepQ} onChange={setSleepQ} name="sleep_quality" testid="wellness-sleep-quality" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Energy / fatigue</label>
            <Slider value={energy} onChange={setEnergy} name="energy" testid="wellness-energy" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Muscle soreness</label>
            <Slider value={soreness} onChange={setSoreness} name="soreness" testid="wellness-soreness" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Mood / motivation</label>
            <Slider value={mood} onChange={setMood} name="mood" testid="wellness-mood" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="e.g. tweaked knee in last session"
              className="form-input mt-1 w-full"
              data-testid="wellness-notes"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <button onClick={onClose} className="pill" data-testid="wellness-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="pill active disabled:opacity-60"
                  data-testid="wellness-save">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
